import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  Paper,
  List,
  ListItemButton,
  Collapse,
  IconButton,
  Chip,
  Stack,
  InputAdornment,
} from '@mui/material';
import {
  ExpandMore,
  ChevronRight,
  Add,
  CreateNewFolder,
  Folder,
  FolderOpen,
  UnfoldMore,
  UnfoldLess,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axios from 'axios';
import { ACCENT, ACCENT_LIGHT, ACCENT_BORDER, panelArrowSx } from '../ProductCategory/categoryUiShared';

const collectExpandableIds = (nodes = []) => {
  const ids = [];
  const walk = (list) => {
    list.forEach((node) => {
      if (node.children?.length) {
        ids.push(node._id);
        walk(node.children);
      }
    });
  };
  walk(nodes);
  return ids;
};

export default function CategoryTreeView({ categoryHierarchy = [], budObject, onSelect }) {
  const [categories, setCategories] = useState([]);
  const [expandedIds, setExpandedIds] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [selectedParentId, setSelectedParentId] = useState(null);
  const [fieldErr, setFieldErr] = useState({});
  const [selectedPath, setSelectedPath] = useState([]);

  useEffect(() => {
    setCategories(categoryHierarchy);
    setExpandedIds(collectExpandableIds(categoryHierarchy));
  }, [categoryHierarchy]);

  const toggleExpand = (_id) => {
    setExpandedIds((prev) =>
      prev.includes(_id) ? prev.filter((e) => e !== _id) : [...prev, _id]
    );
  };

  const expandAll = () => setExpandedIds(collectExpandableIds(categories));
  const collapseAll = () => setExpandedIds([]);

  const handleAddCategory = async () => {
    setFieldErr({});

    if (!selectedParentId && !budObject?._id) {
      setFieldErr({ category: 'Select a parent category' });
      toast.error('Select a parent category');
      return;
    }

    if (!newCategoryName.trim()) {
      setFieldErr({ category: 'Enter category name' });
      toast.error('Enter category name');
      return;
    }

    try {
      const payload = {
        bud_id: selectedParentId || budObject._id,
        bud_name: newCategoryName.trim(),
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/product/add-category`,
        payload
      );

      const createdCategory = response.data?.data;

      if (!createdCategory?._id || !createdCategory?.parent_id) {
        throw new Error('Invalid response from server');
      }

      const newCategory = {
        _id: createdCategory._id,
        text: createdCategory.text,
        children: [],
      };

      const addChild = (nodes) =>
        nodes.map((node) => {
          if (node._id === createdCategory.parent_id) {
            return { ...node, children: [...(node.children || []), newCategory] };
          }
          if (node.children?.length) {
            return { ...node, children: addChild(node.children) };
          }
          return node;
        });

      setCategories((prev) => addChild(prev));
      setNewCategoryName('');
      setExpandedIds((prev) =>
        prev.includes(createdCategory.parent_id) ? prev : [...prev, createdCategory.parent_id]
      );
      toast.success('Category added');
    } catch (err) {
      console.error(err);
      toast.error('Failed to add category');
    }
  };

  const getCategoryPath = (nodes, targetId, path = []) => {
    for (const node of nodes) {
      const newPath = [...path, node.text || node.name || node.title || 'Unnamed'];
      if (node._id === targetId) return newPath;
      if (node.children?.length) {
        const result = getCategoryPath(node.children, targetId, newPath);
        if (result) return result;
      }
    }
    return null;
  };

  const handleSelectNode = (node) => {
    setSelectedParentId(node._id);
    setSelectedPath(getCategoryPath(categories, node._id) || []);
    onSelect(node);
  };

  const renderTree = (nodes, depth = 0) =>
    nodes.map((node) => {
      const isExpanded = expandedIds.includes(node._id);
      const hasChildren = node.children?.length > 0;
      const isSelected = selectedParentId === node._id;

      return (
        <Box key={node._id}>
          <ListItemButton
            selected={isSelected}
            onClick={() => handleSelectNode(node)}
            sx={{
              pl: 1.5 + depth * 2,
              py: 0.75,
              borderRadius: 1,
              mb: 0.25,
              border: '1px solid',
              borderColor: isSelected ? ACCENT_BORDER : 'transparent',
              bgcolor: isSelected ? ACCENT_LIGHT : 'transparent',
              '&.Mui-selected': {
                bgcolor: ACCENT_LIGHT,
                '&:hover': { bgcolor: 'rgba(112, 40, 218, 0.12)' },
              },
            }}
          >
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (hasChildren) toggleExpand(node._id);
              }}
              sx={{
                mr: 0.5,
                color: hasChildren ? 'text.secondary' : 'transparent',
                pointerEvents: hasChildren ? 'auto' : 'none',
              }}
            >
              {hasChildren ? (
                isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />
              ) : (
                <Box sx={{ width: 20 }} />
              )}
            </IconButton>

            {hasChildren ? (
              isExpanded ? (
                <FolderOpen sx={{ fontSize: 18, color: ACCENT, mr: 1 }} />
              ) : (
                <Folder sx={{ fontSize: 18, color: 'text.secondary', mr: 1 }} />
              )
            ) : (
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: isSelected ? ACCENT : 'text.disabled',
                  mr: 1.5,
                  ml: 0.5,
                }}
              />
            )}

            <Typography
              variant="body2"
              sx={{ flex: 1, fontWeight: isSelected ? 600 : 400, color: isSelected ? ACCENT : 'text.primary' }}
            >
              {node.text}
            </Typography>
          </ListItemButton>

          {hasChildren && (
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ ml: 2 + depth * 2, pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                {renderTree(node.children, depth + 1)}
              </Box>
            </Collapse>
          )}
        </Box>
      );
    });

  const hasParentSelected = Boolean(selectedParentId || budObject?._id);

  return (
    <Card elevation={3} sx={{ p: { xs: 2, md: 2.5 }, width: '100%', mb: 2 }}>
      <Typography fontFamily="Aeonik" sx={{ fontSize: '22px', fontWeight: 700 }}>
        Categories
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 0.5 }}>
        Select a category to edit below, or add a new sub-category inside it.
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { md: 'stretch' },
          gap: { xs: 2, md: 1.5 },
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            p: 2,
            borderRadius: 2,
            borderColor: hasParentSelected ? ACCENT_BORDER : 'divider',
            bgcolor: '#fafafa',
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>
                Select Category
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Click to select
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5}>
              <IconButton size="small" onClick={expandAll} aria-label="Expand all">
                <UnfoldMore fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={collapseAll} aria-label="Collapse all">
                <UnfoldLess fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>

          <Box
            sx={{
              maxHeight: 280,
              overflowY: 'auto',
              bgcolor: '#fff',
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: hasParentSelected ? ACCENT_BORDER : 'divider',
              p: 0.5,
            }}
          >
            {categories.length > 0 ? (
              <List disablePadding dense>{renderTree(categories)}</List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <Folder sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No categories yet
                </Typography>
              </Box>
            )}
          </Box>

          {selectedPath.length > 0 && (
            <>
              <Typography variant="caption" color="text.secondary" display="block" mt={1.5} mb={0.5}>
                Selected
              </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5} alignItems="center">
              {selectedPath.map((segment, i) => (
                <React.Fragment key={`${segment}-${i}`}>
                  {i > 0 && <ChevronRight sx={{ fontSize: 14, color: 'text.disabled' }} />}
                  <Chip
                    label={segment}
                    size="small"
                    sx={
                      i === selectedPath.length - 1
                        ? { bgcolor: ACCENT, color: '#fff', fontWeight: 600 }
                        : { borderColor: ACCENT_BORDER, color: ACCENT }
                    }
                    variant={i === selectedPath.length - 1 ? 'filled' : 'outlined'}
                  />
                </React.Fragment>
              ))}
            </Stack>
            </>
          )}
        </Paper>

        <Box sx={panelArrowSx}>→</Box>

        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            p: 2,
            borderRadius: 2,
            borderColor: hasParentSelected ? ACCENT_BORDER : 'divider',
            bgcolor: hasParentSelected ? ACCENT_LIGHT : '#fafafa',
            transition: 'background-color 0.2s, border-color 0.2s',
          }}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            New Sub-Category
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
            {hasParentSelected ? 'Will be added inside the selected category' : 'Select a category from the left first'}
          </Typography>

          {selectedPath.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={0.5} alignItems="center" mb={2}>
              {selectedPath.map((segment, i) => (
                <React.Fragment key={`parent-${segment}-${i}`}>
                  {i > 0 && <ChevronRight sx={{ fontSize: 14, color: 'text.disabled' }} />}
                  <Chip label={segment} size="small" variant="outlined" sx={{ borderColor: ACCENT_BORDER, color: ACCENT }} />
                </React.Fragment>
              ))}
              <ChevronRight sx={{ fontSize: 14, color: ACCENT }} />
              <Chip
                label={newCategoryName.trim() || '…'}
                size="small"
                sx={{ bgcolor: ACCENT, color: '#fff', fontWeight: 600 }}
              />
            </Stack>
          )}

          <TextField
            fullWidth
            size="small"
            label="Category name"
            placeholder="e.g. Gold Rings"
            name="category"
            error={!!fieldErr.category}
            helperText={fieldErr.category}
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
            disabled={!hasParentSelected}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CreateNewFolder sx={{ fontSize: 20, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1.5 }}
          />

          <Button
            variant="contained"
            fullWidth
            startIcon={<Add />}
            onClick={handleAddCategory}
            disabled={!hasParentSelected || !newCategoryName.trim()}
            sx={{
              textTransform: 'none',
              bgcolor: ACCENT,
              fontWeight: 600,
              '&:hover': { bgcolor: '#5a1fb0' },
              '&.Mui-disabled': { bgcolor: 'grey.200' },
            }}
          >
            Add Category
          </Button>
        </Paper>
      </Box>
    </Card>
  );
}
